import { useMemo, useState } from "react";
import { useAppSelector } from "../../../redux/hooks";
import { PerformanceProgressBars } from "./PerformanceProgressBars";
import { formatCurrency, formatNumber, formatPercent } from "../../../utils/formatNumber";
import { css } from "../../../../styled-system/css";
import Card from "../../../components/core/basic/Card";
import { Flex, VStack } from "../../../../styled-system/jsx";
import Loading from "../../../components/core/blocks/Loading";
import { useLiveDashboardFilters } from "../api/useLiveDashboardFilters";
import { useLiveOrders } from "../api/useLiveOrders";
import { useCurrentDayAdSpend } from "../api/useCurrentDayAdSpend";
import { useGetV4CategoryReportTotals } from "../../perf_reports/api/category/v4CategoryReportTotals";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";

const TodayComparisonSummary = () => {
  const { t } = useTranslation(["live"]);
  const [daysAgo, setDaysAgo] = useState(1);

  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const filters = useLiveDashboardFilters(daysAgo);
  const liveOrdersQuery = useLiveOrders(filters);
  const currentDayAdSpendQuery = useCurrentDayAdSpend(filters);

  const categoryTotalOptionsGetter = useGetV4CategoryReportTotals();
  const categoryTotalsReportQuery = useQuery({
    ...categoryTotalOptionsGetter({
      start: moment(filters.start_date).startOf("day").toDate(),
      end: moment(filters.end_date).startOf("day").toDate(),
      preStart: moment(filters.pre_start_date).startOf("day").toDate(),
      preEnd: moment(filters.pre_end_date).startOf("day").toDate(),
    }),
  });

  const { acos, tacos } = useMemo(() => {
    if (!categoryTotalsReportQuery.data || !currentDayAdSpendQuery.data) {
      return {
        acos: 0,
        tacos: 0,
      };
    }

    let acos = 0;
    let tacos = 0;

    if (currentDayAdSpendQuery.data.attributed_sales_7d > 0) {
      acos = currentDayAdSpendQuery.data.cost / currentDayAdSpendQuery.data.attributed_sales_7d;

      if (
        (liveOrdersQuery.data?.order_item_value && liveOrdersQuery.data.order_item_value > 0) ||
        (categoryTotalsReportQuery.data.data.total_sales && categoryTotalsReportQuery.data.data.total_sales > 0)
      ) {
        let totalSales = liveOrdersQuery.data?.order_item_value;
        if (!totalSales) {
          totalSales = categoryTotalsReportQuery.data.data.total_sales || 0;
        }
        tacos = currentDayAdSpendQuery.data.cost / totalSales;
      }
    }

    return {
      acos,
      tacos,
    };
  }, [categoryTotalsReportQuery.data, currentDayAdSpendQuery.data, liveOrdersQuery.data]);

  const daysRotation: Record<1 | 7 | 14 | 30, string> = {
    1: t(($) => $.live["Yesterday"]),
    7: t(($) => $.live["7 Days Ago"]),
    14: t(($) => $.live["14 Days Ago"]),
    30: t(($) => $.live["30 Days Ago"]),
  };

  return (
    <Card>
      <Flex className={css({ gap: "medium", justifyContent: "center", fontSize: "large" })}>
        <span
          className={css({
            borderRadius: "5px",
            paddingX: "medium",
            color: "white",
            backgroundColor: "trellisPalette.10",
          })}
        >
          {t(($) => $.live["Today"])}
        </span>
        <span>{t(($) => $.live["VS"])}</span>
        <span
          className={css({
            cursor: "pointer",
            borderRadius: "5px",
            paddingX: "medium",
            color: "white",
            backgroundColor: "trellisPalette.2",
          })}
          onClick={() => {
            if (daysAgo === 1) {
              setDaysAgo(7);
            } else if (daysAgo === 7) {
              setDaysAgo(14);
            } else if (daysAgo === 14) {
              setDaysAgo(30);
            } else {
              setDaysAgo(1);
            }
          }}
        >
          {daysRotation[daysAgo as keyof typeof daysRotation]}
        </span>
      </Flex>
      <VStack gap={"large"} paddingY={"large"}>
        {liveOrdersQuery.isLoading || categoryTotalsReportQuery.isLoading || currentDayAdSpendQuery.isLoading ?
          <Loading fullPage={false} height={"350px"} />
        : <>
            <PerformanceProgressBars
              label={t(($) => $.live["Units Sold"])}
              current={liveOrdersQuery.data?.quantity_ordered || 0}
              previous={categoryTotalsReportQuery.data?.data.total_units_prev || 0}
              format={(value) => formatNumber(value, { decimalPlaces: 0 })}
            />
            <PerformanceProgressBars
              label={t(($) => $.live["Total Sales"])}
              current={liveOrdersQuery.data?.order_item_value || 0}
              previous={categoryTotalsReportQuery.data?.data.total_sales_prev || 0}
              format={(value) => formatCurrency(value, { marketPlace })}
            />
            <PerformanceProgressBars
              label={t(($) => $.live["Ad Spend"])}
              current={currentDayAdSpendQuery.data?.cost || 0}
              previous={categoryTotalsReportQuery.data?.data.cost_prev || 0}
              format={(value) => formatCurrency(value, { marketPlace })}
            />
            <PerformanceProgressBars
              label={t(($) => $.live["Ad Sales"])}
              current={currentDayAdSpendQuery.data?.attributed_sales_7d || 0}
              previous={categoryTotalsReportQuery.data?.data.ad_sales_prev || 0}
              format={(value) => formatCurrency(value, { marketPlace })}
            />
            <PerformanceProgressBars
              label={t(($) => $.live["ACOS"])}
              current={acos}
              previous={categoryTotalsReportQuery.data?.data.acos_prev || 0}
              format={(value) => formatPercent(value)}
            />
            <PerformanceProgressBars
              label={t(($) => $.live["TACOS"])}
              current={tacos}
              previous={categoryTotalsReportQuery.data?.data.total_acos_prev || 0}
              format={(value) => formatPercent(value)}
            />
          </>
        }
      </VStack>
    </Card>
  );
};

export default TodayComparisonSummary;
